/**
 * Document-scoped constraint set store (Faz 6.1). One model-space
 * ConstraintSet per document — the OCS `ParametricConstraintSet` shape:
 * constraint records, the named-parameter table, the derived dimension
 * binding cache and the solver's conflict report. Persisted into the .yap
 * cadScene envelope by ProjectFileIO (version field + empty-set fallback
 * for older saves).
 */

import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import {
    DEFAULT_AUTOCONSTRAIN_SETTINGS,
    type AutoConstrainSettings,
    type ConstraintPayload,
    type ConstraintRecord,
    type ConstraintRef,
    type ConstraintSet,
    type DrivingValue,
} from './types';
import { createsCycle, type ParameterDef } from './parameters';
import type { ConstraintKind } from './kinds';

export const EMPTY_CONSTRAINT_SET: ConstraintSet = {
    version: 1,
    constraints: [],
    nextId: 1,
    parameters: {},
    dimensions: {},
    conflicts: [],
};

/** Mutable input for addConstraint (id assigned by the store). */
export interface ConstraintInput {
    kind: ConstraintKind;
    refs: ConstraintRef[];
    driving?: DrivingValue;
    value?: number;
    enabled?: boolean;
    /** Fixed constraints: recorded world points (ref order). */
    fixedPoints?: number[][];
}

interface ConstraintStoreState {
    /** The single model-space set. */
    set: ConstraintSet;
    /** Glyph selection (hit-test result) — DELCONSTRAINT target. */
    selectedConstraintId: string | null;
    /** AUTOCONSTRAIN inference settings (sanitized on write). */
    autoConstrain: AutoConstrainSettings;

    addConstraint: (input: ConstraintInput) => string;
    addConstraints: (inputs: ConstraintInput[]) => string[];
    removeConstraints: (ids: string[]) => void;
    updateConstraint: (id: string, patch: Partial<ConstraintRecord>) => void;
    setConstraints: (records: ConstraintRecord[], nextId?: number) => void;

    /** Dimension binding (OCS set.dimensions). */
    bindDimension: (constraintId: string, dimensionEntityId: string) => void;
    unbindDimension: (constraintId: string) => void;

    /** Solver reporting. */
    applySolveResult: (conflicts: string[], values: Record<string, number>) => void;

    /** Parameter table writes. Throws via ParamError when a cycle would form. */
    setParameter: (name: string, def: ParameterDef) => void;
    removeParameter: (name: string) => void;
    renameParameters: (map: Record<string, string>) => void;

    /** Glyph selection + visibility. */
    setSelectedConstraint: (id: string | null) => void;
    setConstraintsHidden: (ids: string[], hidden: boolean) => void;
    setAllConstraintsHidden: (hidden: boolean) => void;

    setAutoConstrainSetting: <K extends keyof AutoConstrainSettings>(key: K, value: AutoConstrainSettings[K]) => void;

    /** Document swap / persistence. */
    applyPayload: (payload: ConstraintPayload | null | undefined) => void;
    serializePayload: () => ConstraintPayload;
    resetSet: () => void;
}

function sanitizeNumber(v: unknown, fallback: number): number {
    const n = typeof v === 'number' ? v : Number(v);
    return Number.isFinite(n) ? n : fallback;
}

/** OCS sanitization: non-finite settings collapse to defaults. */
function sanitizeSettings(raw: Partial<AutoConstrainSettings> | undefined): AutoConstrainSettings {
    if (!raw) return { ...DEFAULT_AUTOCONSTRAIN_SETTINGS };
    return {
        tangentMustSharePoint: raw.tangentMustSharePoint !== false,
        perpendicularMustIntersect: raw.perpendicularMustIntersect !== false,
        distanceTolerance: Math.max(1e-6, sanitizeNumber(raw.distanceTolerance, DEFAULT_AUTOCONSTRAIN_SETTINGS.distanceTolerance)),
        angleToleranceDeg: Math.max(1e-6, sanitizeNumber(raw.angleToleranceDeg, DEFAULT_AUTOCONSTRAIN_SETTINGS.angleToleranceDeg)),
        equalEnabled: raw.equalEnabled === true,
    };
}

const prefixOf = (kind: ConstraintKind): 'gc' | 'dc' =>
    ['Distance', 'DistanceX', 'DistanceY', 'Angle', 'Radius', 'Diameter'].includes(kind) ? 'dc' : 'gc';

export const useConstraintStore = create<ConstraintStoreState>()(
    immer((set, get) => ({
    set: { ...EMPTY_CONSTRAINT_SET, constraints: [], parameters: {}, dimensions: {}, conflicts: [] },
    selectedConstraintId: null,
    autoConstrain: { ...DEFAULT_AUTOCONSTRAIN_SETTINGS },

    addConstraint: (input) => {
        const id = `${prefixOf(input.kind)}${get().set.nextId}`;
        const record: ConstraintRecord = {
            id,
            kind: input.kind,
            refs: input.refs,
            driving: input.driving,
            value: input.value,
            enabled: input.enabled ?? true,
            fixedPoints: input.fixedPoints,
        };
        set((state) => {
            state.set.constraints.push(record);
            state.set.nextId += 1;
            state.set.conflicts = state.set.conflicts.filter((c) => c !== id);
        });
        return id;
    },

    addConstraints: (inputs) => {
        const ids: string[] = [];
        set((state) => {
            for (const input of inputs) {
                const cid = `${prefixOf(input.kind)}${state.set.nextId}`;
                state.set.constraints.push({
                    id: cid,
                    kind: input.kind,
                    refs: input.refs,
                    driving: input.driving,
                    value: input.value,
                    enabled: input.enabled ?? true,
                    fixedPoints: input.fixedPoints,
                });
                state.set.nextId += 1;
                ids.push(cid);
            }
        });
        return ids;
    },

    removeConstraints: (ids) => {
        const doomed = new Set(ids);
        set((state) => {
            state.set.constraints = state.set.constraints.filter((c) => !doomed.has(c.id));
            for (const id of ids) delete state.set.dimensions[id];
            state.set.conflicts = state.set.conflicts.filter((c) => !doomed.has(c));
            if (state.selectedConstraintId && doomed.has(state.selectedConstraintId)) {
                state.selectedConstraintId = null;
            }
        });
    },

    updateConstraint: (id, patch) => {
        set((state) => {
            const c = state.set.constraints.find((r) => r.id === id);
            if (c) Object.assign(c, patch);
        });
    },

    setConstraints: (records, nextId) => {
        set((state) => {
            state.set.constraints = records;
            if (nextId !== undefined) state.set.nextId = nextId;
            state.set.conflicts = [];
        });
    },

    bindDimension: (constraintId, dimensionEntityId) => {
        set((state) => { state.set.dimensions[constraintId] = dimensionEntityId; });
    },

    unbindDimension: (constraintId) => {
        set((state) => { delete state.set.dimensions[constraintId]; });
    },

    applySolveResult: (conflicts, values) => {
        set((state) => {
            state.set.conflicts = conflicts;
            for (const c of state.set.constraints) {
                if (values[c.id] !== undefined) c.value = values[c.id];
            }
        });
    },

    setParameter: (name, def) => {
        const existing = get().set.parameters[name];
        const expr = def.expression;
        // Cycle guard on EVERY write (new + existing): p1=p2, p2=p1 must be
        // refused before it poisons the solve (Faz 6.9 contract).
        if (createsCycle(name, expr, get().set.parameters)) {
            throw new Error(`cycle:${name}`);
        }
        set((state) => {
            state.set.parameters[name] = { expression: expr, description: def.description ?? existing?.description };
        });
    },

    removeParameter: (name) => {
        set((state) => {
            delete state.set.parameters[name];
            // Constraints referencing the removed parameter fall back to
            // their last measured value so the solver keeps a literal.
            for (const c of state.set.constraints) {
                if (c.driving && 'named' in c.driving && c.driving.named === name) {
                    c.driving = { literal: c.value ?? 0 };
                }
            }
        });
    },

    renameParameters: (map) => {
        set((state) => {
            for (const [from, to] of Object.entries(map)) {
                const def = state.set.parameters[from];
                if (!def || !to || to === from) continue;
                delete state.set.parameters[from];
                state.set.parameters[to] = def;
                for (const c of state.set.constraints) {
                    if (c.driving && 'named' in c.driving && c.driving.named === from) {
                        c.driving = { named: to };
                    }
                }
            }
        });
    },

    setSelectedConstraint: (id) => set({ selectedConstraintId: id }),
    setConstraintsHidden: (ids, hidden) => {
        const doomed = new Set(ids);
        set((state) => {
            for (const c of state.set.constraints) {
                if (doomed.has(c.id)) c.hidden = hidden || undefined;
            }
        });
    },
    setAllConstraintsHidden: (hidden) => {
        set((state) => {
            for (const c of state.set.constraints) {
                c.hidden = hidden || undefined;
            }
        });
    },

    setAutoConstrainSetting: (key, value) => {
        set((state) => {
            state.autoConstrain = sanitizeSettings({ ...state.autoConstrain, [key]: value });
        });
    },

    applyPayload: (payload) => {
        if (!payload || payload.version !== 1 || !Array.isArray(payload.constraints)) {
            // Backwards compatibility: missing/unknown payloads load an
            // empty set (Faz 6.1 contract).
            set((state) => {
                state.set = { ...EMPTY_CONSTRAINT_SET, constraints: [], parameters: {}, dimensions: {}, conflicts: [] };
                state.selectedConstraintId = null;
            });
            return;
        }
        const hidden = payload.hidden ?? {};
        set((state) => {
            state.set = {
                version: 1,
                constraints: payload.constraints.map((c) => ({
                    ...c,
                    hidden: hidden[c.id] || c.hidden || undefined,
                })),
                nextId: typeof payload.nextId === 'number' && payload.nextId > 0 ? payload.nextId : payload.constraints.length + 1,
                parameters: payload.parameters ?? {},
                dimensions: payload.dimensions ?? {},
                conflicts: [],
            };
            state.selectedConstraintId = null;
        });
    },

    serializePayload: () => {
        const s = get().set;
        const hidden: Record<string, boolean> = {};
        let any = false;
        for (const c of s.constraints) {
            if (c.hidden) { hidden[c.id] = true; any = true; }
        }
        return {
            version: 1,
            constraints: s.constraints.map((c) => ({ ...c })),
            nextId: s.nextId,
            parameters: { ...s.parameters },
            dimensions: { ...s.dimensions },
            hidden: any ? hidden : undefined,
        };
    },

    resetSet: () => {
        set((state) => {
            state.set = { ...EMPTY_CONSTRAINT_SET, constraints: [], parameters: {}, dimensions: {}, conflicts: [] };
            state.selectedConstraintId = null;
        });
    },
    }))
);

/** Convenience selectors. */
export const selectConstraintById = (id: string | null) => (state: ConstraintStoreState) =>
    id ? state.set.constraints.find((c) => c.id === id) ?? null : null;

export const selectConstraintsForEntity = (entityId: string) => (state: ConstraintStoreState) =>
    state.set.constraints.filter((c) => c.refs.some((r) => r.entityId === entityId));
